import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, errMsg, fmtDate, fmtDateTime, openBlob } from "../lib/api";
import { Field, FileUploader, StatusBadge, Chip } from "./common";
import { Modal, SideCalc, ComponentsView } from "./shared";
import PrestationEditor from "./declare/PrestationEditor";
import { CONTROL_STATES, QTY_FIELDS } from "../lib/labels";
import { DateField } from "./DateFields";
import { EmailCompanyButton } from "./AdminActions";
import { useLang } from "../lib/i18n";

const H = ({ k, className = "" }) => {
  const { t } = useLang();
  return <h4 className={`font-bold uppercase ${className}`}>{t(k)}</h4>;
};

const goTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" });

function AutoChecks({ errors, onFix }) {
  if (!errors) return null;
  if (!errors.length) return <div className="text-sm text-emerald-800 bg-emerald-50 rounded-lg p-2" data-testid="auto-check-ok">Contrôle automatique : aucune erreur, la prestation peut être validée.</div>;
  return (
    <div className="rounded-lg border border-red-300 bg-red-50 p-3 space-y-2" data-testid="auto-check-panel">
      <div className="font-bold text-red-800 text-sm">Contrôle automatique : {errors.length} erreur(s) à corriger avant validation</div>
      {errors.map((e) => (
        <div key={e.code} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`auto-check-${e.code}`}>
          <span className="tl-mono text-xs bg-white border border-red-200 rounded px-1.5 py-0.5">{e.code}</span>
          <span className="flex-1 text-red-900">{e.message}</span>
          <button type="button" className="tl-btn tl-btn-ghost tl-btn-sm" onClick={() => onFix(e.section)} data-testid={`auto-check-fix-${e.code}`}>Corriger</button>
        </div>
      ))}
    </div>
  );
}

const toForm = (p) => ({ mixed: p.mixed, observations: p.observations || "", components: p.components.map((c) => ({
  type: c.type, zone: c.zone || "", qty: Object.fromEntries(QTY_FIELDS.map(([k]) => [k, c.qty[k] ?? ""])),
  special: { ...(c.special || {}), destination: c.special?.destination || "", address: c.special?.address || "", km: c.special?.km ?? "",
    photos: (c.special?.photo_ids || []).map((id) => ({ id, filename: "photo" })),
    hours: c.special?.minutes != null ? Math.floor(c.special.minutes / 60) : "", minutes: c.special?.minutes != null ? c.special.minutes % 60 : "" } })) });
const n = (v) => (v === "" || v == null ? null : Number(v));
const fromForm = (f) => ({ mixed: f.mixed, observations: f.observations, components: f.components.map((c) => ({
  type: c.type, zone: c.zone, qty: Object.fromEntries(QTY_FIELDS.map(([k]) => [k, n(c.qty[k])])),
  special: ["special", "special_fret", "express_plus"].includes(c.type) ? { ...c.special, km: n(c.special.km), minutes: c.special.hours === "" && c.special.minutes === "" ? null : Number(c.special.hours || 0) * 60 + Number(c.special.minutes || 0) } : null })) });

function TiffPages({ file }) {
  const [imgs, setImgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/files/${file.id}/pages`);
      const out = [];
      for (let i = 1; i <= Math.min(data.count || 0, 50); i++) {
        const r = await api.get(`/files/${file.id}/page/${i}`, { responseType: "blob" });
        out.push(URL.createObjectURL(r.data));
      }
      setImgs(out);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div>
      <button className="tl-btn tl-btn-ghost tl-btn-sm" onClick={load} disabled={busy} data-testid={`tiff-pages-${file.id}`}>{busy ? "Chargement…" : `Afficher les pages${file.page_count ? ` (${file.page_count})` : ""}`}</button>
      {imgs.map((s, i) => <div key={i} className="mt-2"><div className="text-xs text-slate-500">Page {i + 1}</div><img src={s} alt={`Page ${i + 1}`} className="border rounded" /></div>)}
    </div>
  );
}

function Files({ d, p, reload }) {
  const [page, setPage] = useState({});
  const link = async (fid) => {
    try {
      await api.post(`/declarations/${d.id}/prestations/${p.id}/link-file`, { file_id: fid, page: page[fid] || "" });
      toast.success("Rapport rattaché");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2">
      <H k="ctl.files" />
      {(d.files || []).map((f) => (
        <div key={f.id} className="bg-slate-50 rounded-lg p-2 text-sm space-y-1">
          <div className="flex gap-2 items-center flex-wrap"><Chip>{f.kind}</Chip>
            <button className="underline" onClick={() => openBlob(`/files/${f.id}/download`)} data-testid={`open-file-${f.id}`}>{f.filename}</button>
            {f.kind === "pm_report" && (<>
              <input className="tl-input !min-h-[36px] w-28" placeholder="Page(s)" value={page[f.id] || ""} onChange={(e) => setPage({ ...page, [f.id]: e.target.value })} data-testid={`link-page-${f.id}`} />
              <button className="tl-btn tl-btn-dark tl-btn-sm" onClick={() => link(f.id)} data-testid={`link-file-${f.id}`}>Rattacher à {p.ref}</button>
            </>)}
          </div>
          {f.view === "other" ? <div className="text-xs text-slate-500" data-testid={`no-preview-${f.id}`}>Aperçu indisponible pour ce format — cliquez sur le nom pour télécharger</div>
            : (f.view === "pdf" || f.content_type === "application/pdf" || f.page_count > 1 || ["image/tiff", "image/heic", "image/heif"].includes(f.content_type)) && <TiffPages file={f} />}
        </div>
      ))}
      {p.pm_links?.length > 0 && <div className="text-xs">Rattachés : {p.pm_links.map((l, i) => (
        <span key={`${l.file_id}-${i}`}>{i > 0 && " · "}{l.lot_id ? <a className="underline" href={`/pm-reports?lot=${l.lot_id}`} data-testid={`pm-link-open-${i}`}>{l.filename} p.{l.page || "?"}</a> : `${l.filename} p.${l.page || "?"}`}</span>
      ))}</div>}
      {p.pm_observation && <div className="text-xs bg-slate-50 rounded p-2" data-testid="pm-observation">{p.pm_observation}</div>}
      <FileUploader kind="pm_report" declarationId={d.id} files={[]} onChange={() => reload()} label="Déposer un PM Report" testid="control-upload-pm" />
    </div>
  );
}

function ControlForm({ d, p, reload }) {
  const [status, setStatus] = useState(p.control_status);
  const [ecarts, setEcarts] = useState(p.ecarts || []);
  const [note, setNote] = useState(p.control_note || "");
  const [ack, setAck] = useState(!!p.flags_ack);
  const [errors, setErrors] = useState(null);
  const flags = [...(d.flags || []), ...(p.flags || [])];
  useEffect(() => {
    api.get(`/declarations/${d.id}/prestations/${p.id}/checks`).then((r) => setErrors(r.data.errors));
  }, [d.id, p.id]);
  const setE = (i, patch) => setEcarts(ecarts.map((e, j) => (j === i ? { ...e, ...patch } : e)));
  const save = async () => {
    try {
      await api.post(`/declarations/${d.id}/prestations/${p.id}/control`, { status, ecarts, note, ack_flags: ack });
      toast.success("Contrôle enregistré");
      reload();
    } catch (e) {
      const errs = e?.response?.data?.detail?.errors;
      if (errs) setErrors(errs);
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-2">
      <H k="ctl.manual" />
      <AutoChecks errors={errors} onFix={goTo} />
      <p className="text-xs text-slate-500">Examinez toutes les pages et annotations manuscrites (bas de page, marges). Les mentions barrées sont exclues et notées ; une information illisible reste à clarifier.</p>
      {ecarts.map((e, i) => (
        <div key={i} id={`ecart-${i}`} className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-center" data-testid={`ecart-${i}`}>
          <input className="tl-input" placeholder="Champ" value={e.field} onChange={(x) => setE(i, { field: x.target.value })} />
          <input className="tl-input" placeholder="Déclaré" value={e.declared ?? ""} onChange={(x) => setE(i, { declared: x.target.value })} />
          <input className="tl-input" placeholder="PM Report (page)" value={e.report ?? ""} onChange={(x) => setE(i, { report: x.target.value })} />
          <input className="tl-input" placeholder="Note" value={e.note} onChange={(x) => setE(i, { note: x.target.value })} />
          <label className="text-sm flex gap-2 items-center"><input type="checkbox" checked={!!e.resolved} onChange={(x) => setE(i, { resolved: x.target.checked })} data-testid={`ecart-resolved-${i}`} /> Résolu</label>
        </div>
      ))}
      <button className="tl-btn tl-btn-ghost tl-btn-sm" onClick={() => setEcarts([...ecarts, { field: "", declared: "", report: "", note: "", resolved: false }])} data-testid="add-ecart-button"><Plus size={14} /> Écart</button>
      {flags.length > 0 && (
        <label id="flags-ack" className="flex gap-2 items-start text-sm bg-amber-50 rounded-lg p-2">
          <input type="checkbox" className="mt-1" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="flags-ack-checkbox" />
          J'ai vérifié les {flags.length} alerte(s) de cette prestation.
        </label>
      )}
      <div className="grid sm:grid-cols-2 gap-2">
        <select className="tl-select" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="control-status-select">
          {Object.entries(CONTROL_STATES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input className="tl-input" placeholder="Note de contrôle" value={note} onChange={(e) => setNote(e.target.value)} data-testid="control-note-input" />
      </div>
      <button className="tl-btn tl-btn-primary" onClick={save} data-testid="control-save-button">Enregistrer le contrôle</button>
    </div>
  );
}

function Correction({ d, p, reload }) {
  const [f, setF] = useState(toForm(p));
  const [reason, setReason] = useState("");
  const save = async () => {
    try {
      await api.patch(`/declarations/${d.id}/prestations/${p.id}`, { ...fromForm(f), reason });
      toast.success("Correction historisée");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const recalc = async () => {
    try {
      await api.post(`/declarations/${d.id}/prestations/${p.id}/recalculate`, { reason });
      toast.success("Montants recalculés");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2" id="correction-section">
      <H k="ctl.correction" />
      <PrestationEditor p={f} pi={0} onChange={setF} />
      <Field label="Motif (obligatoire)"><input className="tl-input" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="correction-reason-input" /></Field>
      <div className="flex gap-2 flex-wrap">
        <button className="tl-btn tl-btn-dark" disabled={!reason.trim()} onClick={save} data-testid="correction-save-button">Enregistrer la correction</button>
        <button className="tl-btn tl-btn-ghost" onClick={recalc} data-testid="recalculate-button">Recalculer avec les tarifs actuels</button>
      </div>
    </div>
  );
}

function PmAmount({ d, p, reload }) {
  const ov = p.pm_overrides || {};
  const [v, setV] = useState({ ups: ov.ups ?? "", sub: ov.sub ?? "" });
  const [reason, setReason] = useState("");
  if (!p.components.some((c) => c.type === "pm")) return null;
  const save = async (side, reset) => {
    try {
      await api.post(`/declarations/${d.id}/prestations/${p.id}/pm-amount`, { side, amount: reset || v[side] === "" ? null : Number(v[side]), reason });
      toast.success(reset ? "Montant PM remis au tarif" : "Montant PM modifié");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2 rounded-lg border border-red-300 bg-red-50 p-3" id="pm-amount-section" data-testid="pm-amount-section">
      <H k="ctl.pm" className="text-red-700" />
      <p className="text-xs text-slate-600">Laissez vide pour appliquer le tarif de la matrice.</p>
      {[["sub", "Sous-traitant"], ["ups", "UPS"]].map(([k, label]) => (
        <div key={k} className="grid grid-cols-3 gap-2 items-center">
          <span className="text-sm font-semibold">{label}{ov[k] != null && <span className="text-xs text-red-700"> (modifié)</span>}</span>
          <input type="number" min="0" step="0.01" className="tl-input" placeholder="Tarif matrice" value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`pm-amount-${k}-input`} />
          <div className="flex gap-1">
            <button className="tl-btn tl-btn-dark tl-btn-sm" disabled={!reason.trim()} onClick={() => save(k)} data-testid={`pm-amount-${k}-save`}>Enregistrer</button>
            {ov[k] != null && <button className="tl-btn tl-btn-ghost tl-btn-sm" disabled={!reason.trim()} onClick={() => save(k, true)} data-testid={`pm-amount-${k}-reset`}>Tarif</button>}
          </div>
        </div>
      ))}
      <input className="tl-input" placeholder="Motif (obligatoire)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="pm-amount-reason" />
    </div>
  );
}

function Billing({ d, p, reload }) {
  const [b, setB] = useState(p.billing);
  const sides = [["ups", "Facturation UPS", ["non_facture", "facture"]], ["sub", "Facture sous-traitant", ["non_facture", "facture"]], ["sub_payment", "Paiement sous-traitant", ["non_paye", "paye"]]];
  const save = async (side) => {
    try {
      await api.post(`/declarations/${d.id}/prestations/${p.id}/billing`, { side, ...b[side] });
      toast.success("Statut enregistré");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2">
      <H k="ctl.billing" />
      {d.is_late && <p className="text-sm text-amber-800">Rattrapage : vérifiez les références existantes et régularisez uniquement le côté manquant.</p>}
      {sides.map(([k, label, opts]) => (
        <div key={k} className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-center" data-testid={`billing-${k}`}>
          <span className="text-sm font-semibold">{label}</span>
          <select className="tl-select" value={b[k].status} onChange={(e) => setB({ ...b, [k]: { ...b[k], status: e.target.value } })} data-testid={`billing-${k}-status`}>
            {opts.map((o) => <option key={o} value={o}>{{ non_facture: "Non facturé", facture: "Facturé", non_paye: "Non payé", paye: "Payé" }[o]}</option>)}
          </select>
          <input className="tl-input" placeholder="Référence" value={b[k].ref} onChange={(e) => setB({ ...b, [k]: { ...b[k], ref: e.target.value } })} data-testid={`billing-${k}-ref`} />
          <DateField value={b[k].date} onChange={(v) => setB({ ...b, [k]: { ...b[k], date: v } })} testid={`billing-${k}-date`} />
          <button className="tl-btn tl-btn-ghost tl-btn-sm" onClick={() => save(k)} data-testid={`billing-${k}-save`}>Enregistrer</button>
        </div>
      ))}
    </div>
  );
}

function History({ pid }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get(`/audit/${pid}`).then((r) => setRows(r.data));
  }, [pid]);
  if (!rows.length) return null;
  return (
    <details className="text-xs"><summary className="font-bold uppercase cursor-pointer">Historique ({rows.length})</summary>
      {rows.map((r) => <div key={r.id} className="border-t py-1">{fmtDateTime(r.at)} · {r.author_name} · {r.field} · motif : {r.reason || "—"}<div className="tl-mono text-slate-500 break-all">{JSON.stringify(r.old)} → {JSON.stringify(r.new)}</div></div>)}
    </details>
  );
}

export default function ControlPanel({ did, pid, onClose }) {
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    api.get(`/declarations/${did}`).then((r) => setD(r.data));
  }, [did, tick]);
  if (!d) return null;
  const p = d.prestations.find((x) => x.id === pid);
  const reload = () => setTick(tick + 1);
  return (
    <Modal title={`Contrôle ${p.ref}`} onClose={onClose} testid="control-panel" wide>
      <div className="grid lg:grid-cols-2 gap-5" key={tick}>
        <div className="space-y-4">
          <div className="text-sm">{fmtDate(d.work_date)} · {d.company_name} · {d.driver_name} · <span className="tl-mono">{d.employee_id} / {d.route_id}</span></div>
          <div className="flex gap-2 flex-wrap"><StatusBadge status={p.control_status} />{d.is_late && <Chip tone="red">Rattrapage</Chip>}<EmailCompanyButton companyId={d.company_id} subject={`TourLink — ${p.ref}`} /></div>
          {[...(d.flags || []), ...(p.flags || [])].map((f, i) => <div key={i} className="text-sm text-red-700 bg-red-50 rounded p-2" data-testid={`flag-${i}`}>{f}</div>)}
          {(d.correction_requests || []).map((c) => <div key={c.id} className="text-sm bg-amber-50 rounded p-2">Demande de {c.by} : {c.message}</div>)}
          <ComponentsView p={p} />
          <SideCalc calc={p.calc.ups} label="Montant UPS" testid="panel-ups-calc" />
          <SideCalc calc={p.calc.sub} label="Montant sous-traitant" testid="panel-sub-calc" />
          <Files d={d} p={p} reload={reload} />
          {d.returns?.length > 0 && <div className="text-sm">Retours : {d.returns.map((r) => `${r.quantity} × ${r.motif === "AUTRE" ? r.motif_other : r.motif}`).join(", ")}</div>}
        </div>
        <div className="space-y-6">
          <ControlForm d={d} p={p} reload={reload} />
          <PmAmount d={d} p={p} reload={reload} />
          <Billing d={d} p={p} reload={reload} />
          <Correction d={d} p={p} reload={reload} />
          <History pid={p.id} />
        </div>
      </div>
    </Modal>
  );
}
